import BaseReport from '../shared/base_report';
import React, { useState } from 'react';
import { savedReportsUrl } from '../shared/util';
import {
  faDollarSign,
  faCalendar,
  faMagnifyingGlass,
  faChairOffice,
  faFence,
  faApartment,
  faFlag,
  faHotel,
  faStore,
  faIndustry
} from '@fortawesome/pro-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import type { InventoryReportProps } from '../shared/types';

const searchTextRansackLabel = 'address_or_name_or_city_state_zip_or_city_cont';

const filters = (filterValues) => {
  let baseFilters = {
    search: {
      ransackLabel: searchTextRansackLabel,
      label: 'Address, Name, State, or Zip',
      type: 'search',
      isDefault: true,
      category: 'Search',
      showLabel: false
    },
    availability: {
      ransackLabel: 'property_availability',
      label: 'Availability',
      type: 'multiselect',
      isDefault: true,
      category: 'Availability',
      showLabel: false
    },
    dealStatus: {
      ransackLabel: 'status_id_in',
      label: 'Status',
      type: 'multiselect',
      isDefault: true,
      category: 'Listing'
    },
    propertyType: {
      ransackLabel: 'property_type_id_in',
      label: 'Property Type',
      childLabel: 'propertySubtype',
      type: 'accordion',
      isDefault: true,
      category: 'Property Type',
      showLabel: false
    },
    broker: {
      ransackLabel: 'property_projects_users_user_id_in',
      label: 'Broker',
      type: 'dropdown',
      isDefault: true,
      category: 'Company',
      maxNumberOfOptions: 12
    },
    company: {
      ransackLabel: 'property_company_id_in',
      label: 'Company',
      type: 'dropdown',
      isDefault: true,
      category: 'Company',
      maxNumberOfOptions: 12
    },
    buildingSize: {
      ransackLabel: 'property_research_property_building_size',
      label: 'Building Size (SF)',
      type: 'range',
      isDefault: true,
      category: 'Building'
    },
    salePrice: {
      ransackLabel: 'property_financial_statements_sale_price',
      label: 'Sale Price ($)',
      type: 'range',
      isDefault: true,
      category: 'Financials'
    },
    leaseRate: {
      ransackLabel: 'property_lease_spaces_lease_rate',
      label: 'Lease Rate ($/SF/Year)',
      type: 'range',
      isDefault: true,
      category: 'Financials'
    },
    propertySubtype: {
      ransackLabel: 'property_research_property_property_type_mappings_property_subtype_id_in',
      label: 'Property Subtype',
      type: 'accordion',
      isDefault: false
    },
    office: {
      ransackLabel: 'property_primary_broker_company_office_id_in',
      label: 'Office',
      type: 'dropdown',
      isDefault: false,
      category: 'Company',
      maxNumberOfOptions: 12
    },
    numberOfUnits: {
      ransackLabel: 'property_research_property_number_of_units',
      label: 'Number of Units',
      type: 'range',
      isDefault: false,
      category: 'Building'
    },
    lotSize: {
      ransackLabel: 'property_research_property_lot_size',
      label: 'Lot Size (Acres)',
      type: 'range',
      isDefault: false,
      category: 'Building'
    },
    spaceSize: {
      ransackLabel: 'property_lease_spaces_space_size',
      label: 'Space Size (SF)',
      type: 'range',
      isDefault: false,
      category: 'Building'
    },
    market: {
      ransackLabel: 'property_research_property_market_in',
      label: 'Market',
      type: 'dropdown',
      isDefault: false,
      category: 'Listing'
    },
    submarket: {
      ransackLabel: 'property_research_property_submarket_in',
      label: 'Submarket',
      type: 'dropdown',
      isDefault: false,
      category: 'Listing'
    },
    createdAt: {
      ransackLabel: 'property_created_at',
      label: 'Date Created',
      type: 'date',
      isDefault: false,
      category: 'Dates'
    },
    saleListedOn: {
      ransackLabel: 'property_sale_listed_on',
      label: 'Sale Listed On',
      type: 'date',
      isDefault: false,
      category: 'Dates'
    },
    leaseListedOn: {
      ransackLabel: 'property_lease_listed_on',
      label: 'Lease Listed On',
      type: 'date',
      isDefault: false,
      category: 'Dates'
    },
    createdBy: {
      ransackLabel: 'property_research_property_created_by_name_in',
      label: 'Created By',
      type: 'dropdown',
      isDefault: false,
      titleOverride: 'user',
      category: 'Company',
      maxNumberOfOptions: 12
    },
    updatedAt: {
      ransackLabel: 'property_updated_at',
      label: 'Date Updated',
      type: 'date',
      isDefault: false,
      category: 'Dates'
    },
    lastUpdatedBy: {
      ransackLabel: 'property_research_property_last_updated_by_name_in',
      label: 'Last Updated By',
      type: 'dropdown',
      isDefault: false,
      titleOverride: 'user',
      category: 'Company',
      maxNumberOfOptions: 12
    },
    closedDate: {
      ransackLabel: 'property_close_date',
      label: 'Closed Date',
      type: 'date',
      isDefault: false,
      category: 'Dates'
    },
    statusChangedToActive: {
      ransackLabel: 'property_sale_status_active_on',
      label: 'Sale Status Changed to Active',
      type: 'date',
      isDefault: false,
      category: 'Dates'
    },
    buildingClass: {
      ransackLabel: 'property_research_property_building_class_in',
      label: 'Building Class',
      type: 'multiselect',
      isDefault: false,
      category: 'Listing'
    },
    expirationDate: {
      ransackLabel: 'property_projects_agreements_expiration_date',
      label: 'Expiration Date',
      type: 'date',
      isDefault: false,
      category: 'Dates',
      childFilter: {
        key: 'includeExpired',
        ransackLabel: 'include_expired_listings',
        label: 'Show Expired Listings',
      }
    }
  };

  if (filterValues.customFields && filterValues.customFields.options.length > 0) {
    const customFilters = filterValues.customFields.options.map((field) => {
      return (
        {
          [field.label]: {
            ransackLabel: `custom_field_schema-${field.id}`,
            label: field.label,
            type: field.valueType,
            isDefault: false,
            category: 'Custom Fields',
            showLabel: true,
            options: field.options
          }
        }
      );
    });

    baseFilters = customFilters.reduce(
      (filters, customFilter) => ({ ...filters, ...customFilter }),
      baseFilters
    );
  }

  return baseFilters;
};

interface FilterItem {
  label: string;
  icon?: IconDefinition;
  accordionIcons?: Array<{label: string; icon: IconDefinition}>;
  minPlaceholder?: string;
  maxPlaceholder?: string;
}

const allFilterOrder = (filterValues): Array<Array<FilterItem>> => {
  const filterOrder: Array<Array<FilterItem>> = [
    [
      { 'label': 'Search', 'icon': faMagnifyingGlass },
      { 'label': 'Property Type',
        'accordionIcons': [
          { 'label': 'Office', 'icon': faChairOffice },
          { 'label': 'Retail', 'icon': faStore },
          { 'label': 'Industrial', 'icon': faIndustry },
          { 'label': 'Land', 'icon': faFence },
          { 'label': 'Multifamily', 'icon': faApartment },
          { 'label': 'Special Purpose', 'icon': faFlag },
          { 'label': 'Hospitality', 'icon': faHotel },
        ] },
      { 'label': 'Availability' },
      { 'label': 'Company' }
    ]
  ]

  if (filterValues.customFields && filterValues.customFields.options.length > 0) {
    filterOrder.push(
      [
        { 'label': 'Listing' },
        { 'label': 'Financials', 'icon': faDollarSign, 'minPlaceholder': 'From', 'maxPlaceholder': 'To' },
        { 'label': 'Dates', 'icon': faCalendar, 'minPlaceholder': 'From', 'maxPlaceholder': 'To' }
      ]
    );
    filterOrder.push(
      [
        { 'label': 'Building', 'minPlaceholder': 'Min', 'maxPlaceholder': 'Max' },
        { 'label': 'Custom Fields' }
      ]
    );
  } else {
    filterOrder.push(
      [
        { 'label': 'Listing' },
        { 'label': 'Financials', 'icon': faDollarSign, 'minPlaceholder': 'From', 'maxPlaceholder': 'To' }
      ]
    );
    filterOrder.push(
      [
        { 'label': 'Dates', 'icon': faCalendar, 'minPlaceholder': 'From', 'maxPlaceholder': 'To' },
        { 'label': 'Building', 'minPlaceholder': 'Min', 'maxPlaceholder': 'Max' }
      ]
    );
  }

  return filterOrder;
};

const defaultGroupingOptions = {
  group_by: [
    { label: 'Property Type', value: 'property_type' },
    { label: 'Primary Broker', value: 'primary_broker' },
    { label: 'Availability', value: 'availability' }
  ],
  sort_by: [
    { label: 'City', value: 'city' },
    { label: 'Address', value: 'address' },
    { label: 'Broker', value: 'broker' }
  ]
};
const transformRecords = recordResponse => recordResponse.properties;
const reportPath = '/research/inventory_reports';

export default function InventoryReport({
  columns,
  filterValues,
  report,
  reportName,
  reportsPath,
  timeZone,
  isCorporate = false
}: InventoryReportProps) {
  const [savedReport, setSavedReport] = useState(report);

  const csvDownloadPath = isCorporate
    ? '/corporate/properties.csv'
    : '/properties.csv';

  const url = isCorporate
    ? '/corporate/properties?paginate=true&include_meta=true&inventory_report=true'
    : '/properties?paginate=true&include_meta=true&inventory_report=true';

  const updatedSavedReportUrl = () => {
    if (savedReport) {
      return `${savedReportsUrl}/${savedReport.id}`;
    }
  };

  return (
    <BaseReport
      allFilterOrder={allFilterOrder(filterValues)}
      baseReportName={reportName}
      columns={columns}
      csvDownloadPath={csvDownloadPath}
      defaultGroupingOptions={defaultGroupingOptions}
      disablePdf={isCorporate}
      filters={
        (
          isCorporate
            ? ({ broker, ...rest }) => rest
            : ({ company, ...rest }) => rest
        )(filters(filterValues))
      }
      filterValues={filterValues}
      reportClass="Reports::InventoryReport"
      reportPath={reportPath}
      reportsPath={reportsPath}
      reportType="property"
      savedReport={savedReport}
      savedReportsUrl={savedReportsUrl}
      searchTextRansackLabel={searchTextRansackLabel}
      timeZone={timeZone}
      transformRecords={transformRecords}
      updatedSavedReportUrl={updatedSavedReportUrl()}
      url={url}
      onSetSavedReport={setSavedReport}
    />
  );
}
